import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {createRefetchContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {FlatfileButton} from '@flatfile/react';
import {Table, Dropdown} from 'web-components';
import MissionControlHeader from './mission_control_header';

const FlatfileStyle = styled.div`
	max-height: 100vh;
	min-height: 100vh;
	.content {
		padding: 0px 20px;
		display: flex;
		align-items: center;
	}
	.dropdown {
		width: 400px;
		margin-bottom: 24px;
		margin-right: 20px;
	}
`;

const Flatfile = ({viewer, relay}) => {
	const [selectedCompanyId, setSelectedCompanyId] = useState([]);
	const [selectedProjectId, setSelectedProjectId] = useState([]);
	const [fetchedCompanyId, setFetchedCompanyId] = useState(null);
	const [selectedProject, setSelectedProject] = useState(null);
	const [dateFormat, setDateFormat] = useState(['MM-DD-YYYY']);

	const tableData = {
		rows: viewer.missionControlData.dataMigrationLog,
	};

	const setCompanyId = companyId => {
		setSelectedCompanyId(companyId);
		setFetchedCompanyId(null);
		relay.refetch({companyId: companyId[0]});
	};

	const setProject = projectId => {
		setSelectedProjectId(projectId);
		setSelectedProject(viewer?.missionControlData?.dataMigration?.projects?.find(project => project.id === projectId[0]));
	};

	const booleanRecordHook = (record, property, out, defaultValue = 'false') => {
		if (record[property] != null) {
			let trimmedValue = record[property].trim().toLowerCase();

			// Default to false
			if (trimmedValue === '') {
				trimmedValue = defaultValue;
			}

			const isTrue = ['true', 'yes', '1'].findIndex(v => trimmedValue === v.toLowerCase()) > -1;
			const isFalse = ['false', 'no', '0'].findIndex(v => trimmedValue === v.toLowerCase()) > -1;
			const knownValue = isTrue || isFalse;

			out[property] = {
				value: !knownValue ? record[property] : isTrue ? 'true' : 'false',
				info: knownValue ? [] : [{message: 'Unknown value! Please use on of the following: true, false, yes, no, 1, 0', level: 'error'}],
			};
		}
		return out;
	};

	const validateDate = (record, property, out) => {
		if (record[property]) {
			let momentDate = moment(record[property], dateFormat[0]);
			let isValidDate = momentDate.isValid();
			out[property] = {
				value: isValidDate ? momentDate.format('YYYY-MM-DD') : record[property],
				info: isValidDate ? [] : [{message: `Value is not a date with format: ${dateFormat[0]}!`, level: 'error'}],
			};
		}
		return out;
	};

	const recordHook = (record, index) => {
		let out = {};
		out = validateDate(record, 'startDate', out);
		out = validateDate(record, 'deadline', out);
		out = validateDate(record, 'milestoneStart', out);
		out = validateDate(record, 'milestoneEnd', out);
		out = validateDate(record, 'sprintStart', out);
		out = validateDate(record, 'sprintEnd', out);
		out = booleanRecordHook(record, 'blocked', out);
		out = booleanRecordHook(record, 'bug', out);
		out = booleanRecordHook(record, 'billable', out);
		out = booleanRecordHook(record, 'highPriority', out);
		if (record.assigned) {
			const activeUserEmails = viewer?.missionControlData?.dataMigration?.activeUserEmails || [];
			const emails = record.assigned.split(/[,;]+/);
			for (var i = emails.length - 1; i >= 0; i--) {
				const email = emails[i].trim();
				if (email !== '' && !activeUserEmails.includes(email)) {
					out.assigned = {
						value: record.assigned,
						info: [
							{
								message: `${email} not found in the database.`,
								level: 'error',
							},
						],
					};
					break;
				}
			}
		}
		if (record.owner) {
			const activeUserEmails = viewer?.missionControlData?.dataMigration?.activeUserEmails || [];
			if (record.owner.trim() !== '' && !activeUserEmails.includes(record.owner.trim())) {
				out.owner = {
					value: record.owner,
					info: [
						{
							message: 'User not found in the database.',
							level: 'error',
						},
					],
				};
			}
		}
		if (record.remaining != null && selectedProject?.autoRemaining) {
			out.remaining = {
				value: record.remaining,
				info: [
					{
						message: 'Project is using automatic remaining calculation. This value will not be used!',
						level: 'warning',
					},
				],
			};
		}
		return out;
	};

	useEffect(() => {
		setFetchedCompanyId(selectedCompanyId[0]);
	}, [viewer.missionControlData.dataMigration]);

	const dateFormatOptions = ['YYYY-MM-DD', 'MM-DD-YYYY', 'DD-MM-YYYY'];

	return (
		<FlatfileStyle>
			<MissionControlHeader viewer={viewer} />
			<div className="content">
				<div className="dropdown">
					Select the date format used in the file:
					<Dropdown selectedItems={dateFormat} width={400} name="Select date format" onSelect={setDateFormat}>
						{dateFormatOptions.map(option => (
							<Dropdown.SingleText key={option} value={option} searchString={option}>
								{option}
							</Dropdown.SingleText>
						))}
					</Dropdown>
				</div>
			</div>
			<div className="content">
				<div className="dropdown">
					Select a company to import tasks for:
					<Dropdown selectedItems={selectedCompanyId} width={400} name="Select company" onSelect={setCompanyId}>
						{viewer.missionControlData.companies.map(company => (
							<Dropdown.SingleText key={company.id} value={company.id} searchString={company.name}>
								{company.name}
							</Dropdown.SingleText>
						))}
					</Dropdown>
				</div>
				{viewer?.missionControlData?.dataMigration?.projects && (
					<div className="dropdown">
						Select a project to import tasks into:
						<Dropdown selectedItems={selectedProjectId} width={400} name="Select project" onSelect={setProject}>
							{viewer.missionControlData.dataMigration.projects.map(project => (
								<Dropdown.SingleText key={project.id} value={project.id} searchString={`P-${project.companyProjectId} ${project.name}`}>
									{`P-${project.companyProjectId} ${project.name}`}
								</Dropdown.SingleText>
							))}
						</Dropdown>
					</div>
				)}
				<div>
					{fetchedCompanyId && selectedProjectId?.length ? (
						<FlatfileButton
							key={fetchedCompanyId + selectedProjectId[0] + dateFormat[0]}
							licenseKey={process.env.FLATFILE_LICENCE_KEY}
							customer={{userId: selectedCompanyId[0]}}
							settings={{
								type: 'Task',
								devMode: true,
								title: `Import tasks into P-${selectedProject?.companyProjectId} ${selectedProject?.name}`,
								fields: [
									{
										label: 'Name',
										key: 'name',
										description: 'Task name',
										validators: [{validate: 'regex_matches', regex: '^.{1,300}$', error: 'Must be between 1 and 300 characters, and cannot contain linebreaks.'}, {validate: 'required'}],
									},
									{label: 'Description', key: 'description'},
									{
										label: 'Milestone Name',
										key: 'milestoneName',
										validators: [
											{validate: 'regex_matches', regex: '^.{1,191}$', error: 'Must be between 1 and 191 characters, and cannot contain linebreaks.'},
											{
												validate: 'required_with',
												fields: ['milestoneStart', 'milestoneEnd'],
											},
										],
									},
									{label: 'Milestone Start Date', key: 'milestoneStart'},
									{label: 'Milestone End Date', key: 'milestoneEnd'},
									{
										label: 'Sprint Name',
										key: 'sprintName',
										validators: [
											{validate: 'regex_matches', regex: '^.{1,191}$', error: 'Must be between 1 and 191 characters, and cannot contain linebreaks.'},
											{
												validate: 'required_with',
												fields: ['sprintStart', 'sprintEnd'],
											},
										],
									},
									{
										label: 'Sprint Start Date',
										key: 'sprintStart',
										validators: [
											{
												validate: 'required_with',
												fields: ['sprintName'],
											},
										],
									},
									{
										label: 'Sprint End Date',
										key: 'sprintEnd',
										validators: [
											{
												validate: 'required_with',
												fields: ['sprintName'],
											},
										],
									},
									{
										label: 'Estimate',
										key: 'estimate',
										description: 'Task Estimate in Minutes',
										validators: [
											{
												validate: 'regex_matches',
												regex: '^-?\\d*$',
												error: 'Must be a number',
											},
										],
									},
									{
										label: 'Remaining',
										key: 'remaining',
										description: 'Task Remaining in Minutes',
										validators: [
											{
												validate: 'regex_matches',
												regex: '^\\d*$',
												error: 'Must be a positive number',
											},
										],
									},
									{
										label: 'Status',
										key: 'status',
										description: 'Task Status',
										type: 'select',
										options:
											selectedProject?.statusColumns?.map(column => ({
												value: atob(column.id).split(':')[1],
												label: column.name,
											})) || [],
									},
									{label: 'Start Date', key: 'startDate', description: 'Task Start Date'},
									{label: 'Deadline', key: 'deadline', description: 'Task Deadline'},
									{label: 'Blocked', key: 'blocked', description: 'Is Task Blocked?'},
									{label: 'Bug', key: 'bug', description: 'Is Task a Bug?'},
									{label: 'Billable', key: 'billable', description: 'Is Task Billable?'},
									{label: 'High Priority', key: 'highPriority', description: 'Is Task High Priority?'},
									{label: 'Assigned', key: 'assigned', description: 'Emails of the users assigned to the task. Comma separated if multiple.'},
									{label: 'Owner', key: 'owner', description: 'Email of the user who owns this task.'},
									{label: 'Labels', key: 'labels', description: 'Labels to apply to this task. Comma separated if multiple.'},
								],
							}}
							// fieldHooks={{
							// 	assigned: assignedFieldHook,
							// 	owner: ownerFieldHook,
							// }}
							onRecordInit={recordHook}
							onRecordChange={recordHook}
							onData={async results => {
								const matchedFields = results.$meta.headers_matched.reduce((acc, cur) => {
									acc.push(cur.matched_key);
									return acc;
								}, []);
								const tasks = results.$data.reduce((acc, cur) => {
									acc.push(cur.data);
									return acc;
								}, []);
								const cleanedTasks = [];
								for (var i = 0; i < tasks.length; i++) {
									const cleanedTask = {...tasks[i]};
									for (const property in tasks[i]) {
										if (!matchedFields.includes(property) || tasks[i][property] === '') {
											delete cleanedTask[property];
										}
									}
									cleanedTasks.push(cleanedTask);
								}

								const headers = new Headers();
								headers.append('Content-Type', 'application/json');
								const data = {
									method: 'POST',
									headers,
									credentials: 'include',
									body: JSON.stringify({
										companyId: atob(fetchedCompanyId).split(':')[1],
										projectId: atob(selectedProjectId[0]).split(':')[1],
										tasks: cleanedTasks,
									}),
								};
								return await fetch(`${process.env.GRAPHQL_ENDPOINT.slice(0, -7)}mission_control/data_migration`, data).then(response => {
									relay.refetch({companyId: selectedCompanyId[0]});
									if (response.ok) {
										return 'Migration successful';
									}
									return 'Migration failed!';
								});
							}}
						>
							Import Tasks
						</FlatfileButton>
					) : null}
				</div>
			</div>
			<div className="migration-log">
				<Table>
					<Table.Header>
						<Table.HeaderColumn visible width="LARGE">
							<Table.HeaderColumn.Title>Company</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
						<Table.HeaderColumn usePadding flex={1} visible width="LARGE">
							<Table.HeaderColumn.Title>Project</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
						<Table.HeaderColumn usePadding visible width="MEDIUM">
							<Table.HeaderColumn.Title>Date</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
						<Table.HeaderColumn usePadding visible width="MEDIUM">
							<Table.HeaderColumn.Title>Migrated By</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
						<Table.HeaderColumn usePadding visible width="MEDIUM">
							<Table.HeaderColumn.Title>Status</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
						<Table.HeaderColumn usePadding flex={3} visible width="MEDIUM">
							<Table.HeaderColumn.Title>Error message</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
					</Table.Header>
					<Table.Rows data={tableData} canExpand={false}>
						{({rowData, tableColumnsProps}) => {
							return (
								<Table.Row {...tableColumnsProps}>
									<Table.Column>{rowData?.company}</Table.Column>
									<Table.Column>{rowData?.project}</Table.Column>
									<Table.Column>{rowData?.date}</Table.Column>
									<Table.Column>{rowData?.person}</Table.Column>
									<Table.Column>{rowData?.status}</Table.Column>
									<Table.Column>{rowData?.errorMessage}</Table.Column>
								</Table.Row>
							);
						}}
					</Table.Rows>
				</Table>
			</div>
		</FlatfileStyle>
	);
};

const FlatfileQuery = graphql`
	query Flatfile_Query($companyId: ID) {
		viewer {
			actualPersonId
			component(name: "flatfile")
			...Flatfile_viewer @arguments(companyId: $companyId)
		}
	}
`;

export {FlatfileQuery};

export default createRefetchContainer(
	withRouter(Flatfile),
	{
		viewer: graphql`
			fragment Flatfile_viewer on Viewer @argumentDefinitions(companyId: {type: "ID"}) {
				id
				...missionControlHeader_viewer
				missionControlData {
					id
					companies {
						id
						companyId
						name
					}
					dataMigration(companyId: $companyId) {
						id
						activeUserEmails
						projects {
							id
							name
							companyProjectId
							autoRemaining
							statusColumns {
								id
								name
							}
						}
					}
					dataMigrationLog {
						id
						company
						project
						date
						person
						status
						errorMessage
					}
				}
			}
		`,
	},
	graphql`
		query FlatfileRefetch_Query($companyId: ID) {
			viewer {
				...Flatfile_viewer @arguments(companyId: $companyId)
			}
		}
	`
);
